import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { ArrowDownTrayIcon, PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

const READ_TYPES = { ALL: 'TODAS', UC: 'UC', USINA: 'USINA' };
const now = new Date();
const EMPTY = {
  id: null,
  tipo_leitura: READ_TYPES.UC,
  consumer_unit_id: '',
  power_plant_id: '',
  competencia_mes: now.getMonth() + 1,
  competencia_ano: now.getFullYear(),
  periodo_inicio: '',
  periodo_fim: '',
  kwh_consumido: '',
  kwh_compensado: '',
  kwh_gerado: '',
  saldo_creditos_final: '',
  valor_total_enel: '',
  considerar_valor_manual: false,
  valor_cobranca_manual: '',
  pdf_path: '',
};
const DEFAULT_FILTERS = {
  tipo_leitura: READ_TYPES.ALL,
  consumer_unit_id: '',
  power_plant_id: '',
  competencia_mes: '',
  competencia_ano: '',
  search: '',
};

const toDateInputValue = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  return text.slice(0, 10);
};

const normalizeReadType = (value, fallback = READ_TYPES.UC) => {
  const text = String(value || '').trim().toUpperCase();
  if (text === READ_TYPES.UC || text === READ_TYPES.USINA) return text;
  return fallback;
};

const getReadTypeLabel = (value) => (normalizeReadType(value) === READ_TYPES.USINA ? 'USINA' : 'UC');
const getReadTypeBadgeClass = (value) => (normalizeReadType(value) === READ_TYPES.USINA ? 'badge-info' : 'badge-success');
const getEntityLabel = (row) => row?.apelido || row?.consumer_unit_label || row?.power_plant_label || row?.numero_instalacao_enel || '-';
const getEnergyKwh = (row) => {
  if (normalizeReadType(row?.tipo_leitura) === READ_TYPES.USINA) return Number(row?.kwh_gerado ?? row?.kwh_compensado ?? 0);
  return Number(row?.kwh_compensado ?? 0);
};
const formatCurrency = (value) => `R$ ${Number(value || 0).toFixed(2)}`;
const formatBillingAction = (action) => {
  const key = String(action || '').toUpperCase();
  if (key === 'CRIADA') return 'Cobranca criada automaticamente';
  if (key === 'ATUALIZADA' || key === 'ATUALIZADA_E_AJUSTES_CANCELADOS') return 'Cobranca recalculada automaticamente';
  if (key === 'AJUSTE_CRIADO') return 'Ajuste de cobranca criado automaticamente';
  if (key === 'AJUSTE_ATUALIZADO') return 'Ajuste de cobranca atualizado automaticamente';
  if (key === 'AJUSTE_REDUZIDO') return 'Ajuste de cobranca reduzido automaticamente';
  if (key === 'AJUSTE_REDUZIDO_COM_CREDITO' || key === 'CREDITO_PENDENTE') return 'Recalculo concluido com credito pendente';
  if (key === 'SEM_AJUSTE') return 'Leitura salva. Nao houve ajuste financeiro';
  return '';
};

const SolarEnelBillsPage = () => {
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [powerPlants, setPowerPlants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importReadType, setImportReadType] = useState(READ_TYPES.UC);
  const [importFiles, setImportFiles] = useState([]);
  const [importPassword, setImportPassword] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [listFilters, setListFilters] = useState(DEFAULT_FILTERS);
  const [chargePreview, setChargePreview] = useState(null);
  const [chargePreviewLoading, setChargePreviewLoading] = useState(false);
  const formReadType = normalizeReadType(form.tipo_leitura);

  const loadLookups = async () => {
    try {
      const [unitsRs, plantsRs] = await Promise.all([axios.get('/api/solar/consumer-units'), axios.get('/api/solar/power-plants')]);
      setUnits(Array.isArray(unitsRs?.data?.items) ? unitsRs.data.items : []);
      setPowerPlants(Array.isArray(plantsRs?.data?.items) ? plantsRs.data.items : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar listas de UCs/usinas');
    }
  };

  const loadBills = async (nextFilters = listFilters) => {
    setLoading(true);
    try {
      const params = {};
      const filterType = String(nextFilters.tipo_leitura || '').trim().toUpperCase();
      if (filterType === READ_TYPES.UC || filterType === READ_TYPES.USINA) params.tipo_leitura = filterType;
      if (nextFilters.consumer_unit_id) params.consumer_unit_id = nextFilters.consumer_unit_id;
      if (nextFilters.power_plant_id) params.power_plant_id = nextFilters.power_plant_id;
      const mes = Number(nextFilters.competencia_mes);
      const ano = Number(nextFilters.competencia_ano);
      if (Number.isInteger(mes) && mes >= 1 && mes <= 12 && Number.isInteger(ano) && ano >= 2000 && ano <= 2100) {
        params.competencia_mes = mes;
        params.competencia_ano = ano;
      }
      const { data } = await axios.get('/api/solar/enel-bills', { params });
      const rows = Array.isArray(data?.items) ? data.items : [];
      setItems(
        rows.map((item) => ({
          ...item,
          tipo_leitura: normalizeReadType(item.tipo_leitura),
          periodo_inicio: toDateInputValue(item.periodo_inicio),
          periodo_fim: toDateInputValue(item.periodo_fim),
        }))
      );
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar faturas ENEL');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLookups();
    loadBills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredItems = useMemo(() => {
    const search = String(listFilters.search || '').trim().toLowerCase();
    if (!search) return items;
    return items.filter((item) =>
      [item.apelido, item.numero_instalacao_enel, item.customer_nome, item.consumer_unit_label, item.power_plant_label, item.tipo_leitura]
        .map((value) => String(value || '').toLowerCase())
        .join(' ')
        .includes(search)
    );
  }, [items, listFilters.search]);

  useEffect(() => {
    if (!showModal || formReadType !== READ_TYPES.UC) {
      setChargePreview(null);
      setChargePreviewLoading(false);
      return undefined;
    }

    const competenciaMes = Number(form.competencia_mes);
    const competenciaAno = Number(form.competencia_ano);
    const hasCompetencia =
      Number.isInteger(competenciaMes) &&
      competenciaMes >= 1 &&
      competenciaMes <= 12 &&
      Number.isInteger(competenciaAno) &&
      competenciaAno >= 2000 &&
      competenciaAno <= 2100;

    if (!form.consumer_unit_id || !hasCompetencia) {
      setChargePreview(null);
      setChargePreviewLoading(false);
      return undefined;
    }

    if (form.considerar_valor_manual && !String(form.valor_cobranca_manual || '').trim()) {
      setChargePreview({
        enabled: false,
        error: false,
        message: 'Informe o valor manual para calcular o preview',
      });
      setChargePreviewLoading(false);
      return undefined;
    }

    const payload = {
      tipo_leitura: READ_TYPES.UC,
      consumer_unit_id: form.consumer_unit_id,
      competencia_mes: competenciaMes,
      competencia_ano: competenciaAno,
      kwh_consumido: Number(form.kwh_consumido || 0),
      kwh_compensado: Number(form.kwh_compensado || 0),
      considerar_valor_manual: Boolean(form.considerar_valor_manual),
      valor_cobranca_manual:
        form.considerar_valor_manual && String(form.valor_cobranca_manual || '').trim()
          ? Number(form.valor_cobranca_manual)
          : null,
    };

    let cancelled = false;
    const timer = setTimeout(async () => {
      setChargePreviewLoading(true);
      try {
        const { data } = await axios.post('/api/solar/enel-bills/preview-charge', payload);
        if (cancelled) return;
        setChargePreview(data?.preview || null);
      } catch (error) {
        if (cancelled) return;
        setChargePreview({
          enabled: false,
          error: true,
          message: error.response?.data?.error || 'Nao foi possivel calcular o preview da cobranca',
        });
      } finally {
        if (!cancelled) setChargePreviewLoading(false);
      }
    }, 280);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    showModal,
    formReadType,
    form.consumer_unit_id,
    form.competencia_mes,
    form.competencia_ano,
    form.kwh_consumido,
    form.kwh_compensado,
    form.considerar_valor_manual,
    form.valor_cobranca_manual,
  ]);

  const openNew = () => {
    const defaultReadType = listFilters.tipo_leitura === READ_TYPES.USINA ? READ_TYPES.USINA : READ_TYPES.UC;
    setForm({ ...EMPTY, tipo_leitura: defaultReadType });
    setChargePreview(null);
    setShowModal(true);
  };

  const openEdit = (item) => {
    const readType = normalizeReadType(item.tipo_leitura);
    setForm({
      id: item.id,
      tipo_leitura: readType,
      consumer_unit_id: item.consumer_unit_id || '',
      power_plant_id: item.power_plant_id || '',
      competencia_mes: item.competencia_mes,
      competencia_ano: item.competencia_ano,
      periodo_inicio: toDateInputValue(item.periodo_inicio),
      periodo_fim: toDateInputValue(item.periodo_fim),
      kwh_consumido: item.kwh_consumido ?? '',
      kwh_compensado: item.kwh_compensado ?? '',
      kwh_gerado: item.kwh_gerado ?? item.kwh_compensado ?? '',
      saldo_creditos_final: item.saldo_creditos_final ?? '',
      valor_total_enel: item.valor_total_enel ?? '',
      considerar_valor_manual: false,
      valor_cobranca_manual: '',
      pdf_path: item.pdf_path || '',
    });
    setChargePreview(null);
    setShowModal(true);
  };

  const save = async () => {
    try {
      const readType = normalizeReadType(form.tipo_leitura);
      const payload = {
        tipo_leitura: readType,
        competencia_mes: Number(form.competencia_mes),
        competencia_ano: Number(form.competencia_ano),
        periodo_inicio: form.periodo_inicio || null,
        periodo_fim: form.periodo_fim || null,
        kwh_consumido: Number(form.kwh_consumido || 0),
        saldo_creditos_final: Number(form.saldo_creditos_final || 0),
        valor_total_enel: Number(form.valor_total_enel || 0),
        pdf_path: form.pdf_path || null,
      };

      if (readType === READ_TYPES.USINA) {
        if (!form.power_plant_id) return toast.error('Selecione a usina');
        payload.power_plant_id = form.power_plant_id;
        payload.kwh_gerado = Number(form.kwh_gerado || form.kwh_compensado || 0);
      } else {
        if (!form.consumer_unit_id) return toast.error('Selecione a unidade consumidora');
        payload.consumer_unit_id = form.consumer_unit_id;
        payload.kwh_compensado = Number(form.kwh_compensado || 0);
        payload.gerar_cobranca = true;
        payload.considerar_valor_manual = Boolean(form.considerar_valor_manual);
        if (payload.considerar_valor_manual) {
          const manualValue = Number(form.valor_cobranca_manual);
          if (!Number.isFinite(manualValue) || manualValue < 0) {
            return toast.error('Informe um valor manual de cobranca valido');
          }
          payload.valor_cobranca_manual = manualValue;
        }
      }

      let response;
      if (form.id) {
        response = await axios.put(`/api/solar/enel-bills/${form.id}`, payload);
        toast.success('Leitura ENEL atualizada');
      } else {
        response = await axios.post('/api/solar/enel-bills', payload);
        toast.success('Leitura ENEL cadastrada');
      }

      const billingMsg = formatBillingAction(response?.data?.billing?.action);
      if (billingMsg) toast.success(billingMsg);
      const appliedChargeValue = response?.data?.billing?.values?.valor_aplicado;
      if (appliedChargeValue != null && readType === READ_TYPES.UC) {
        toast.success(`Cobranca aplicada: ${formatCurrency(appliedChargeValue)}`);
      }
      const warnings = response?.data?.billing?.warnings;
      if (Array.isArray(warnings) && warnings.length > 0) {
        toast((t) => (
          <div className="text-sm">
            <p className="font-medium">Atencao no recalculo</p>
            <p className="mt-1">{String(warnings[0])}</p>
            <button className="mt-2 text-primary-600 font-semibold" onClick={() => toast.dismiss(t.id)}>Fechar</button>
          </div>
        ), { duration: 8000 });
      }

      setShowModal(false);
      setForm(EMPTY);
      setChargePreview(null);
      await loadBills();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar leitura ENEL');
    }
  };

  const removeBill = async (item) => {
    if (!item?.id) return;
    const label = `${String(item.competencia_mes).padStart(2, '0')}/${item.competencia_ano} - ${getEntityLabel(item)}`;
    if (!window.confirm(`Deseja excluir a leitura ENEL ${label}?`)) return;
    try {
      await axios.delete(`/api/solar/enel-bills/${item.id}`, { params: { tipo_leitura: normalizeReadType(item.tipo_leitura) } });
      toast.success('Leitura ENEL excluida');
      await loadBills(listFilters);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao excluir leitura ENEL');
    }
  };

  const applyImportBillingOverride = (row, override = {}) => {
    const next = { ...row, ...override };
    if (normalizeReadType(next?.tipo_leitura) !== READ_TYPES.UC) {
      return {
        ...next,
        considerar_valor_manual: false,
        valor_cobranca_manual: '',
      };
    }

    const manualEnabled = Boolean(next.considerar_valor_manual);
    const manualTextRaw = manualEnabled ? String(next.valor_cobranca_manual ?? '').trim() : '';
    const manualNumber = Number(manualTextRaw);
    const hasValidManual = manualEnabled && manualTextRaw !== '' && Number.isFinite(manualNumber) && manualNumber >= 0;
    const preview = next.preview_cobranca && typeof next.preview_cobranca === 'object'
      ? { ...next.preview_cobranca }
      : null;

    if (preview?.enabled) {
      if (hasValidManual) {
        preview.origem_valor = 'MANUAL';
        preview.valor_manual = manualNumber;
        preview.valor_aplicado = manualNumber;
      } else {
        preview.origem_valor = 'CONTRATO';
        preview.valor_manual = null;
        preview.valor_aplicado = preview.valor_calculado;
      }
    }

    return {
      ...next,
      considerar_valor_manual: manualEnabled,
      valor_cobranca_manual: manualEnabled ? manualTextRaw : '',
      preview_cobranca: preview,
    };
  };

  const updateImportBillingOverride = (index, override = {}) => {
    setImportPreview((old) => {
      if (!old || !Array.isArray(old.items)) return old;
      const nextItems = old.items.map((item, itemIndex) => (
        itemIndex === index ? applyImportBillingOverride(item, override) : item
      ));
      return { ...old, items: nextItems };
    });
  };

  const runImportPreview = async () => {
    if (importFiles.length === 0) return toast.error('Selecione ao menos 1 PDF');
    const formData = new FormData();
    formData.append('tipo_leitura', importReadType);
    importFiles.forEach((file) => formData.append('files', file));
    if (importPassword.trim()) formData.append('password', importPassword.trim());
    setPreviewLoading(true);
    try {
      const { data } = await axios.post('/api/solar/enel-bills/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const normalizedItems = Array.isArray(data?.items)
        ? data.items.map((item) =>
            applyImportBillingOverride(item, {
              considerar_valor_manual: Boolean(item?.considerar_valor_manual),
              valor_cobranca_manual:
                item?.valor_cobranca_manual != null ? String(item.valor_cobranca_manual) : '',
            })
          )
        : [];
      setImportPreview(data ? { ...data, items: normalizedItems } : null);
      const summary = data?.summary || {};
      toast.success(`Preview concluido: ${summary.validos || 0} valido(s), ${summary.invalidos || 0} invalido(s)`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao gerar preview da importacao');
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmImport = async () => {
    const rows = Array.isArray(importPreview?.items) ? importPreview.items.filter((item) => item.can_import) : [];
    if (rows.length === 0) return toast.error('Nao ha itens validos para importar');
    const invalidManual = rows.find((item) => {
      if (normalizeReadType(item?.tipo_leitura) !== READ_TYPES.UC) return false;
      if (!item?.considerar_valor_manual) return false;
      const text = String(item?.valor_cobranca_manual ?? '').trim();
      if (!text) return true;
      const numberValue = Number(text);
      return !Number.isFinite(numberValue) || numberValue < 0;
    });
    if (invalidManual) {
      return toast.error(`Valor manual invalido em: ${invalidManual.file_name || getEntityLabel(invalidManual) || 'item da importacao'}`);
    }
    setConfirmLoading(true);
    try {
      const { data } = await axios.post('/api/solar/enel-bills/import/commit', { items: rows, gerar_cobranca: true });
      const summary = data?.summary || {};
      toast.success(`Importacao concluida: ${summary.inseridos || 0} inserido(s), ${summary.atualizados || 0} atualizado(s)`);
      const billingSummary = summary?.cobranca || {};
      if (billingSummary?.habilitada) {
        const totalBillingOps =
          Number(billingSummary.criadas || 0) +
          Number(billingSummary.atualizadas_principal || 0) +
          Number(billingSummary.ajustes_criados || 0) +
          Number(billingSummary.ajustes_atualizados || 0);
        if (totalBillingOps > 0 || Number(billingSummary.sem_ajuste || 0) > 0) {
          toast.success(
            `Cobrancas: ${billingSummary.criadas || 0} criadas, ${billingSummary.atualizadas_principal || 0} atualizadas, ${billingSummary.ajustes_criados || 0} ajustes novos, ${billingSummary.ajustes_atualizados || 0} ajustes alterados`
          );
        }
        if (Number(billingSummary.creditos_pendentes || 0) > 0) {
          toast.error(`Foram identificados ${billingSummary.creditos_pendentes} caso(s) com credito pendente`);
        }
        if (Number(billingSummary.erros || 0) > 0) {
          toast.error(`Falhou gerar cobranca em ${billingSummary.erros} item(ns) importado(s)`);
        }
      }
      setShowImportModal(false);
      setImportPreview(null);
      setImportFiles([]);
      setImportPassword('');
      await loadBills();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao confirmar importacao');
    } finally {
      setConfirmLoading(false);
    }
  };

  const formatPeriod = (row) => {
    const inicio = toDateInputValue(row?.periodo_inicio);
    const fim = toDateInputValue(row?.periodo_fim);
    if (!inicio && !fim) return '-';
    return `${inicio || '-'} ate ${fim || '-'}`;
  };

  const applyFilters = () => loadBills(listFilters);
  const clearFilters = () => {
    setListFilters(DEFAULT_FILTERS);
    return loadBills(DEFAULT_FILTERS);
  };
  const statusBadge = (row) => (row?.can_import ? 'badge-success' : 'badge-danger');
  const handleFilterTypeChange = (value) => {
    const nextType = String(value || READ_TYPES.ALL).toUpperCase();
    setListFilters((old) => ({
      ...old,
      tipo_leitura: nextType,
      consumer_unit_id: nextType === READ_TYPES.USINA ? '' : old.consumer_unit_id,
      power_plant_id: nextType === READ_TYPES.UC ? '' : old.power_plant_id,
    }));
  };

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Solar - Faturas ENEL</h1>
          <p className="text-gray-600">Entrada de dados de consumo ENEL para UCs e usinas</p>
        </div>
        <div className="flex items-center gap-3">
          {loading && <div className="loader h-6 w-6" />}
          <button className="btn-secondary" onClick={() => { setImportReadType(listFilters.tipo_leitura === READ_TYPES.USINA ? READ_TYPES.USINA : READ_TYPES.UC); setImportFiles([]); setImportPassword(''); setImportPreview(null); setShowImportModal(true); }}>
            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
            Importar Faturas ENEL
          </button>
          <button className="btn-primary" onClick={openNew}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Nova Leitura ENEL
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-3 border-b">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Tipo</label>
              <select className="form-select text-sm py-1.5" value={listFilters.tipo_leitura} onChange={(e) => handleFilterTypeChange(e.target.value)}>
                <option value={READ_TYPES.ALL}>Todas</option>
                <option value={READ_TYPES.UC}>UC</option>
                <option value={READ_TYPES.USINA}>USINA</option>
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">Unidade Consumidora</label>
              <select
                className="form-select text-sm py-1.5"
                value={listFilters.consumer_unit_id}
                onChange={(e) => setListFilters((old) => ({ ...old, consumer_unit_id: e.target.value }))}
                disabled={listFilters.tipo_leitura === READ_TYPES.USINA}
              >
                <option value="">Todas</option>
                {units.map((unit) => <option key={unit.id} value={unit.id}>{(unit.apelido || unit.numero_instalacao_enel) + ' - ' + (unit.customer_nome || '-')}</option>)}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">Usina</label>
              <select
                className="form-select text-sm py-1.5"
                value={listFilters.power_plant_id}
                onChange={(e) => setListFilters((old) => ({ ...old, power_plant_id: e.target.value }))}
                disabled={listFilters.tipo_leitura === READ_TYPES.UC}
              >
                <option value="">Todas</option>
                {powerPlants.map((plant) => <option key={plant.id} value={plant.id}>{(plant.nome || plant.numero_instalacao_enel) + ' - ' + (plant.numero_instalacao_enel || '-')}</option>)}
              </select>
            </div>
            <div className="md:col-span-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Mes</label>
              <input
                className="form-input text-sm py-1.5"
                type="number"
                min="1"
                max="12"
                placeholder="MM"
                value={listFilters.competencia_mes}
                onChange={(e) => setListFilters((old) => ({ ...old, competencia_mes: e.target.value }))}
              />
            </div>
            <div className="md:col-span-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Ano</label>
              <input
                className="form-input text-sm py-1.5"
                type="number"
                placeholder="AAAA"
                value={listFilters.competencia_ano}
                onChange={(e) => setListFilters((old) => ({ ...old, competencia_ano: e.target.value }))}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Buscar</label>
              <input
                className="form-input text-sm py-1.5"
                type="search"
                name="solar_enel_filter_search"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="Entidade ou cliente"
                value={listFilters.search}
                onChange={(e) => setListFilters((old) => ({ ...old, search: e.target.value }))}
              />
            </div>
            <div className="md:col-span-12 flex justify-end gap-2 pt-1">
              <button className="btn-primary text-sm py-1.5 px-3" onClick={applyFilters}>Filtrar</button>
              <button className="btn-secondary text-sm py-1.5 px-3" onClick={clearFilters}>Limpar</button>
            </div>
          </div>
        </div>
        <div className="card-header">Leituras ENEL ({filteredItems.length})</div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr><th>Competencia</th><th>Tipo</th><th>Entidade</th><th>Cliente</th><th>kWh Consumido</th><th>kWh Energia</th><th>Valor ENEL</th><th></th></tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id}>
                    <td>{String(item.competencia_mes).padStart(2, '0')}/{item.competencia_ano}</td>
                    <td><span className={getReadTypeBadgeClass(item.tipo_leitura)}>{getReadTypeLabel(item.tipo_leitura)}</span></td>
                    <td>{getEntityLabel(item)}</td>
                    <td>{item.customer_nome || '-'}</td>
                    <td>{Number(item.kwh_consumido || 0).toFixed(2)}</td>
                    <td>{getEnergyKwh(item).toFixed(2)}</td>
                    <td>R$ {Number(item.valor_total_enel || 0).toFixed(2)}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button className="text-primary-600 hover:text-primary-900 p-1" onClick={() => openEdit(item)} title="Editar"><PencilIcon className="h-4 w-4" /></button>
                        <button className="text-danger-600 hover:text-danger-900 p-1" onClick={() => removeBill(item)} title="Excluir"><TrashIcon className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredItems.length === 0 && <tr><td colSpan={8} className="text-center text-gray-500 py-8">Nenhuma leitura ENEL cadastrada</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full">
            <div className="px-6 py-4 border-b"><h3 className="text-lg font-semibold">{form.id ? 'Editar Leitura ENEL' : 'Nova Leitura ENEL'}</h3></div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="form-label">Tipo de Leitura</label>
                <select
                  className="form-select"
                  value={formReadType}
                  disabled={Boolean(form.id)}
                  onChange={(e) => {
                    setForm((old) => ({
                      ...old,
                      tipo_leitura: normalizeReadType(e.target.value),
                      consumer_unit_id: '',
                      power_plant_id: '',
                      considerar_valor_manual: false,
                      valor_cobranca_manual: '',
                    }));
                    setChargePreview(null);
                  }}
                >
                  <option value={READ_TYPES.UC}>UC</option><option value={READ_TYPES.USINA}>USINA</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="form-label">{formReadType === READ_TYPES.USINA ? 'Usina' : 'Unidade Consumidora'}</label>
                {formReadType === READ_TYPES.USINA ? (
                  <select className="form-select" value={form.power_plant_id} onChange={(e) => setForm({ ...form, power_plant_id: e.target.value })}>
                    <option value="">Selecione</option>
                    {powerPlants.map((plant) => <option key={plant.id} value={plant.id}>{(plant.nome || plant.numero_instalacao_enel) + ' - ' + (plant.numero_instalacao_enel || '-')}</option>)}
                  </select>
                ) : (
                  <select className="form-select" value={form.consumer_unit_id} onChange={(e) => setForm({ ...form, consumer_unit_id: e.target.value })}>
                    <option value="">Selecione</option>
                    {units.map((u) => <option key={u.id} value={u.id}>{(u.apelido || u.numero_instalacao_enel) + ' - ' + (u.customer_nome || '-')}</option>)}
                  </select>
                )}
              </div>
              <div><label className="form-label">Competencia Mes</label><input className="form-input" type="number" min="1" max="12" value={form.competencia_mes} onChange={(e) => setForm({ ...form, competencia_mes: e.target.value })} /></div>
              <div><label className="form-label">Competencia Ano</label><input className="form-input" type="number" value={form.competencia_ano} onChange={(e) => setForm({ ...form, competencia_ano: e.target.value })} /></div>
              <div><label className="form-label">Valor Total ENEL</label><input className="form-input" type="number" step="0.01" value={form.valor_total_enel} onChange={(e) => setForm({ ...form, valor_total_enel: e.target.value })} /></div>
              <div><label className="form-label">Periodo Inicio</label><input className="form-input" type="date" value={form.periodo_inicio} onChange={(e) => setForm({ ...form, periodo_inicio: e.target.value })} /></div>
              <div><label className="form-label">Periodo Fim</label><input className="form-input" type="date" value={form.periodo_fim} onChange={(e) => setForm({ ...form, periodo_fim: e.target.value })} /></div>
              <div><label className="form-label">kWh Consumido</label><input className="form-input" type="number" step="0.01" value={form.kwh_consumido} onChange={(e) => setForm({ ...form, kwh_consumido: e.target.value })} /></div>
              <div>
                <label className="form-label">{formReadType === READ_TYPES.USINA ? 'kWh Gerado' : 'kWh Compensado'}</label>
                <input className="form-input" type="number" step="0.01" value={formReadType === READ_TYPES.USINA ? form.kwh_gerado : form.kwh_compensado} onChange={(e) => setForm((old) => ({ ...old, [formReadType === READ_TYPES.USINA ? 'kwh_gerado' : 'kwh_compensado']: e.target.value }))} />
              </div>
              <div><label className="form-label">Saldo Creditos Final</label><input className="form-input" type="number" step="0.01" value={form.saldo_creditos_final} onChange={(e) => setForm({ ...form, saldo_creditos_final: e.target.value })} /></div>
              {formReadType === READ_TYPES.UC && (
                <>
                  <div className="md:col-span-2">
                    <label className="form-label">Calculo da Cobranca</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
                      <label className="flex items-center gap-2 text-sm text-gray-700 border rounded-md px-3 py-2">
                        <input
                          type="radio"
                          name="charge_source_mode"
                          checked={!form.considerar_valor_manual}
                          onChange={() => setForm((old) => ({ ...old, considerar_valor_manual: false }))}
                        />
                        Usar regra do contrato
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-700 border rounded-md px-3 py-2">
                        <input
                          type="radio"
                          name="charge_source_mode"
                          checked={Boolean(form.considerar_valor_manual)}
                          onChange={() => setForm((old) => ({ ...old, considerar_valor_manual: true }))}
                        />
                        Definir valor manual
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="form-label">Valor Manual da Cobranca</label>
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.valor_cobranca_manual}
                      disabled={!form.considerar_valor_manual}
                      onChange={(e) => setForm((old) => ({ ...old, valor_cobranca_manual: e.target.value }))}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-4 py-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-emerald-900">Preview da Cobranca</p>
                        {chargePreviewLoading && <span className="text-xs text-emerald-700">Calculando...</span>}
                      </div>
                      {!chargePreviewLoading && chargePreview?.error && (
                        <p className="text-sm text-red-700 mt-2">{chargePreview.message || 'Nao foi possivel gerar preview'}</p>
                      )}
                      {!chargePreviewLoading && !chargePreview?.error && chargePreview?.enabled && (
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3 text-sm">
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-emerald-700">Valor Contrato</p>
                            <p className="font-semibold text-emerald-900">{formatCurrency(chargePreview.valor_calculado)}</p>
                          </div>
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-emerald-700">Valor Aplicado</p>
                            <p className="font-semibold text-emerald-900">{formatCurrency(chargePreview.valor_aplicado)}</p>
                          </div>
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-emerald-700">Origem</p>
                            <p className="font-semibold text-emerald-900">{chargePreview.origem_valor === 'MANUAL' ? 'Manual' : 'Contrato'}</p>
                          </div>
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-emerald-700">Vencimento Previsto</p>
                            <p className="font-semibold text-emerald-900">{chargePreview.data_vencimento_prevista || '-'}</p>
                          </div>
                        </div>
                      )}
                      {!chargePreviewLoading && !chargePreview && (
                        <p className="text-sm text-emerald-800 mt-2">Preencha UC e competencia para visualizar o valor previsto da cobranca.</p>
                      )}
                      {!chargePreviewLoading && chargePreview?.message && !chargePreview?.error && (
                        <p className="text-sm text-emerald-800 mt-2">{chargePreview.message}</p>
                      )}
                      {!chargePreviewLoading && Array.isArray(chargePreview?.warnings) && chargePreview.warnings.length > 0 && (
                        <p className="text-xs text-amber-700 mt-2">{chargePreview.warnings[0]}</p>
                      )}
                    </div>
                  </div>
                </>
              )}
              <div className="md:col-span-3"><label className="form-label">PDF path (opcional)</label><input className="form-input" value={form.pdf_path} onChange={(e) => setForm({ ...form, pdf_path: e.target.value })} /></div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end space-x-3">
              <button className="btn-secondary" onClick={() => { setShowModal(false); setChargePreview(null); }}>Cancelar</button>
              <button className="btn-primary" onClick={save}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Importar Faturas ENEL (PDF)</h3>
              <p className="text-sm text-gray-600 mt-1">Selecione o tipo de leitura, envie os PDFs e confira o resumo antes de importar.</p>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Tipo da Leitura</label>
                  <select className="form-select" value={importReadType} onChange={(e) => { setImportReadType(normalizeReadType(e.target.value)); setImportPreview(null); }}>
                    <option value={READ_TYPES.UC}>UC</option><option value={READ_TYPES.USINA}>USINA</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Arquivos PDF</label>
                  <input className="form-input" type="file" accept=".pdf,application/pdf" multiple onChange={(e) => setImportFiles(Array.from(e.target.files || []))} />
                  <p className="text-xs text-gray-500 mt-1">{importFiles.length} arquivo(s) selecionado(s)</p>
                </div>
                <div>
                  <label className="form-label">Senha dos PDFs (opcional)</label>
                  <input
                    className="form-input"
                    type="password"
                    name="solar_enel_pdf_password"
                    autoComplete="new-password"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    placeholder="Informe se as faturas estiverem protegidas"
                    value={importPassword}
                    onChange={(e) => setImportPassword(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3"><button className="btn-primary" onClick={runImportPreview} disabled={previewLoading}>{previewLoading ? 'Lendo PDFs...' : 'Ler PDFs e Gerar Resumo'}</button></div>
              {importPreview && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="card"><div className="card-body"><p className="text-xs text-gray-500">Total</p><p className="text-lg font-semibold">{importPreview.summary?.total || 0}</p></div></div>
                    <div className="card"><div className="card-body"><p className="text-xs text-gray-500">Validos</p><p className="text-lg font-semibold text-green-700">{importPreview.summary?.validos || 0}</p></div></div>
                    <div className="card"><div className="card-body"><p className="text-xs text-gray-500">Invalidos</p><p className="text-lg font-semibold text-red-700">{importPreview.summary?.invalidos || 0}</p></div></div>
                    <div className="card"><div className="card-body"><p className="text-xs text-gray-500">Com alertas</p><p className="text-lg font-semibold text-yellow-700">{importPreview.summary?.com_alertas || 0}</p></div></div>
                  </div>
                  <div className="table-container">
                    <table className="table">
                      <thead><tr><th>Arquivo</th><th>Tipo</th><th>Status</th><th>Entidade Identificada</th><th>Competencia</th><th>Periodo</th><th>kWh Cons.</th><th>kWh Energia</th><th>Saldo Cred.</th><th>Valor ENEL</th><th>Prev. Cobranca</th><th>Override</th><th>Observacoes</th></tr></thead>
                      <tbody>
                        {(importPreview.items || []).map((row, index) => (
                          <tr key={`${row.file_name}-${index}`}>
                            <td>{row.file_name}</td>
                            <td><span className={getReadTypeBadgeClass(row.tipo_leitura)}>{getReadTypeLabel(row.tipo_leitura)}</span></td>
                            <td><span className={statusBadge(row)}>{row.can_import ? 'PRONTO' : 'ERRO'}</span></td>
                            <td>{getEntityLabel(row)}</td>
                            <td>{row.competencia_mes && row.competencia_ano ? `${String(row.competencia_mes).padStart(2, '0')}/${row.competencia_ano}` : '-'}</td>
                            <td>{formatPeriod(row)}</td>
                            <td>{row.kwh_consumido != null ? Number(row.kwh_consumido).toFixed(2) : '-'}</td>
                            <td>{row.kwh_compensado != null || row.kwh_gerado != null ? getEnergyKwh(row).toFixed(2) : '-'}</td>
                            <td>{row.saldo_creditos_final != null ? Number(row.saldo_creditos_final).toFixed(2) : '-'}</td>
                            <td>{row.valor_total_enel != null ? `R$ ${Number(row.valor_total_enel).toFixed(2)}` : '-'}</td>
                            <td>
                              {row.preview_cobranca?.enabled
                                ? (
                                  <div className="text-xs">
                                    <div className="font-semibold text-emerald-800">{formatCurrency(row.preview_cobranca.valor_aplicado)}</div>
                                    <div className="text-gray-500">{row.preview_cobranca.origem_valor === 'MANUAL' ? 'Manual' : 'Contrato'}</div>
                                  </div>
                                )
                                : '-'}
                            </td>
                            <td>
                              {normalizeReadType(row?.tipo_leitura) === READ_TYPES.UC && row?.can_import ? (
                                <div className="min-w-[170px] space-y-1">
                                  <label className="inline-flex items-center gap-1 text-xs text-gray-700">
                                    <input
                                      type="checkbox"
                                      checked={Boolean(row.considerar_valor_manual)}
                                      onChange={(e) => updateImportBillingOverride(index, { considerar_valor_manual: e.target.checked })}
                                    />
                                    Valor manual
                                  </label>
                                  <input
                                    className="form-input text-xs py-1"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="R$ 0,00"
                                    disabled={!row.considerar_valor_manual}
                                    value={row.valor_cobranca_manual ?? ''}
                                    onChange={(e) =>
                                      updateImportBillingOverride(index, {
                                        considerar_valor_manual: true,
                                        valor_cobranca_manual: e.target.value,
                                      })
                                    }
                                  />
                                </div>
                              ) : '-'}
                            </td>
                            <td className="text-xs">
                              {Array.isArray(row.errors) && row.errors.length > 0 && <div className="text-red-700">{row.errors.join(' | ')}</div>}
                              {Array.isArray(row.warnings) && row.warnings.length > 0 && <div className="text-yellow-700 mt-1">{row.warnings.join(' | ')}</div>}
                              {(!row.errors || row.errors.length === 0) && (!row.warnings || row.warnings.length === 0) && '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-3">
              <button className="btn-secondary" onClick={() => setShowImportModal(false)} disabled={confirmLoading}>Fechar</button>
              <button className="btn-primary" onClick={confirmImport} disabled={confirmLoading || !Array.isArray(importPreview?.items) || importPreview.items.filter((item) => item.can_import).length === 0}>{confirmLoading ? 'Importando...' : 'Confirmar Importacao'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SolarEnelBillsPage;
